import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';

/**
 * Gate for role-restricted routes. Wrap routes whose scope defines a
 * page-level permission rule, passing the roles allowed to VIEW the page:
 *
 *   <Route element={<RoleRoute allow={[ROLES.ANALYST, ROLES.PRINCIPAL]} />}>
 *     <Route path="/matching" element={<MatchingPage />} />
 *   </Route>
 *
 * Behaviour:
 *   - still loading the session  -> spinner (never a premature redirect)
 *   - not signed in              -> /login, carrying the intended destination
 *   - signed in, wrong role      -> /forbidden (NOT /login: re-authenticating
 *                                  as the same user would not help, and
 *                                  bouncing to login would loop)
 *
 * Edit-level permissions are finer-grained than routing and belong to the
 * page itself; use `useAuth().user.role` there.
 */
export default function RoleRoute({ allow = [] }) {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // An empty `allow` list means "any authenticated user".
  if (allow.length > 0 && !allow.includes(user?.role)) {
    return <Navigate to="/forbidden" replace state={{ from: location }} />;
  }

  return <Outlet />;
}
